/**
 * Service de tracking analytique frontend.
 * Accumule les évènements en mémoire et les envoie par batch.
 *
 * Stratégie :
 * - Buffer local, flush tous les 10s ou quand >= 20 évènements
 * - sendBeacon() sur beforeunload / visibilitychange (best-effort)
 * - Fire-and-forget : ne bloque jamais l'UX
 * - Conditionné au consentement RGPD (flag `enabled`)
 */
import api from './api'

let eventBuffer = []
let flushTimer = null
let sessionId = null
let enabled = false

const FLUSH_INTERVAL_MS = 10_000
const FLUSH_THRESHOLD = 20

/**
 * Générer ou récupérer un ID de session navigateur.
 * Persiste dans sessionStorage (expire à la fermeture de l'onglet).
 */
function getSessionId() {
  if (sessionId) return sessionId
  sessionId = sessionStorage.getItem('bonurba_session_id')
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    sessionStorage.setItem('bonurba_session_id', sessionId)
  }
  return sessionId
}

/**
 * Envoyer le buffer au backend.
 * Utilise sendBeacon si `beacon` est true (page unload), sinon Axios.
 */
async function flush(beacon = false) {
  if (eventBuffer.length === 0) return

  const events = [...eventBuffer]
  eventBuffer = []

  const payload = JSON.stringify({ events })

  if (beacon && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' })
    navigator.sendBeacon('/api/events', blob)
    return
  }

  try {
    await api.post('/api/events', { events })
  } catch (err) {
    // Silencieux : remettre les évènements dans le buffer (best effort, plafond à 100)
    eventBuffer = [...events, ...eventBuffer].slice(0, 100)
    // Ne pas spammer la console — le tracking est fire-and-forget
    if (import.meta.env.DEV && err?.response?.status !== 401) {
      console.debug('[tracking] flush échoué, évènements remis en buffer', err?.response?.status)
    }
  }
}

/**
 * Enregistrer un évènement.
 * Ne bloque jamais — accumule dans le buffer.
 * Retourne immédiatement si le consentement n'est pas donné.
 */
export function trackEvent(eventType, eventData = null, { page } = {}) {
  if (!enabled) return

  eventBuffer.push({
    event_type: eventType,
    event_data: eventData,
    page: page ?? window.location.pathname,
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
  })

  if (eventBuffer.length >= FLUSH_THRESHOLD) {
    flush()
  }
}

/**
 * Initialiser le tracking : timer de flush périodique + listeners page unload.
 * À appeler une seule fois au démarrage de l'application.
 * Ne démarre que si le tracking est activé (consentement donné).
 */
export function initTracking() {
  if (!enabled) return
  if (flushTimer) clearInterval(flushTimer)
  flushTimer = setInterval(() => flush(), FLUSH_INTERVAL_MS)

  window.addEventListener('beforeunload', () => flush(true))
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush(true)
    }
  })
}

/**
 * Arrêter le tracking (cleanup).
 */
export function stopTracking() {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
  flush()
}

/**
 * Activer le tracking (consentement donné).
 */
export function enableTracking() {
  enabled = true
}

/**
 * Désactiver le tracking (consentement retiré ou refusé).
 */
export function disableTracking() {
  enabled = false
  eventBuffer = []
}

/**
 * Le consentement analytique est-il actif ?
 * Permet aux producteurs d'évènements coûteux (RUM carte) de ne rien collecter
 * du tout, plutôt que de collecter pour un `trackEvent` qui se taira.
 */
export function isTrackingEnabled() {
  return enabled
}

/**
 * Forcer l'envoi immédiat du buffer.
 *
 * Nécessaire à tout producteur qui émet depuis un handler `visibilitychange` :
 * le listener interne d'`initTracking` est enregistré au démarrage de
 * l'application, donc il s'exécute en premier et vide un buffer encore vide.
 *
 * @param {boolean} beacon utiliser `sendBeacon` (page en train de disparaître)
 */
export function flushTracking(beacon = false) {
  return flush(beacon)
}
